import { DatasetStringValueMapperDTO } from "components/dashboard/charts/interfaces";
import { DatasetMappersApi } from "../DatasetMappersApi";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient } from "services/HttpService";

export const useGetDatasetMappersData = () =>
  useQueryWrapper([QueryKey.DATASET_CATEGORIES], () =>
    DatasetMappersApi.getAll()
  );

export const useGetDatasetMapperData = (id: number | string | null) => {
  return useQuery({
    queryKey: [QueryKey.DATASET_MAPPERS, id],
    queryFn: async () => {
      if (!id) return null;
      const response = await DatasetMappersApi.getById(id);
      if (!response.data) {
        throw new Error("Failed to fetch mapper data");
      }
      return response.data as DatasetStringValueMapperDTO;
    },
    initialData: () => queryClient.getQueryData([QueryKey.DATASET_MAPPERS, id]) as DatasetStringValueMapperDTO,
  });
};

export const useCreateDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.create(data).then(defaultResponseHandler);
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS], (oldQueryData: any) => {
          return [
              data,
              ...oldQueryData,
          ];
      });
      options.onSuccess!!();
  },
  });
};

export const useUpdateDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.update(data.id, data).then(
        defaultResponseHandler
      );
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS], (oldQueryData: any) => {
          return [
              data,
              ...oldQueryData,
          ];
      });
      options.onSuccess!!();
  },
  });
};

export const useDeleteDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.remove(data.id).then(defaultResponseHandler);
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS], (oldQueryData: any) => {
          const filteredData = oldQueryData.filter((item: DatasetStringValueMapperDTO) => item.id !== data.id);
          return filteredData;
      });
      options.onSuccess!!();
  },
  });
};
